import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { Apollo, gql } from "apollo-angular";
import { firstValueFrom, map } from "rxjs";

import { LowerCasePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, inject, resource } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";

import { SurfCertType, SurfCertTypeConnectionV2 } from "../../../apis/networkdashboard/graphql/graphql";
import { EnabledPipe } from "../../../helpers/enabledpipe/enabledpipe";
import { phraseDetailPath } from "../../../pages/subscription-detail/subscription-detail-redirection";
import { AuthService } from "../../../services/authservice";
import { LoaderComponent } from "../../common/loader/loader";
import { DialogAuthComponent } from "../../dialogs/dialog-auth/dialog-auth";
import { DialogMessageComponent } from "../../dialogs/dialog-message/dialog-message";
import { openSelfServiceDialog } from "../../dialogs/dialog-selfservice/dialog-selfservice.helpers";
import { SelfserviceToggle } from "../../selfservice/selfservice-toggle/selfservice-toggle";
import { ServiceIcon } from "../../subscription-detail/service-icon/service-icon";

const SURF_CERT_QUERY = gql`
  query CertCert($customer: UUID!) {
    surfCertList(searchFilter: { accessCustomerId: $customer }) {
      page {
        subscriptionId
        insync
        customerId
        productType
        productTag
        description
        customerDescription
        surfcertFilterEnabled
      }
    }
  }
`;

@Component({
  selector: "cert-security",
  templateUrl: "cert-security.html",
  styleUrl: "cert-security.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AngularSvgIconModule,
    EnabledPipe,
    LoaderComponent,
    LowerCasePipe,
    SelfserviceToggle,
    ServiceIcon,
    TranslatePipe,
  ],
})
export class CertSecurity {
  private apollo = inject(Apollo);
  protected auth = inject(AuthService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);

  private certResource = resource({
    params: () => ({ customer: this.auth.viewingCustomer().customerId }),
    loader: ({ params }) =>
      firstValueFrom(
        this.apollo
          .query<{ surfCertList: SurfCertTypeConnectionV2 }>({
            query: SURF_CERT_QUERY,
            variables: params,
          })
          .pipe(map(({ data }) => data?.surfCertList?.page ?? [])),
      ),
  });

  protected subscriptions = computed(() => this.certResource.value() ?? []);
  protected loading = this.certResource.isLoading;

  constructor() {
    effect(() => {
      if (this.certResource.value() !== undefined) {
        this.checkForPendingAction();
      }
    });
  }

  get hasCorrectRole(): boolean {
    return this.auth.hasRole(["surfcert-kernel", "Beveiligingsverantwoordelijke", "Infraverantwoordelijke"]);
  }

  private checkForPendingAction() {
    if (this.auth.hasPendingStrongAction("certFilter")) {
      this.auth.clearPendingStrongAction("certFilter");
      const state = this.auth.getSelfserviceState();
      const subscriptionId = state?.subscription?.subscriptionId;
      if (subscriptionId) {
        const subscription = this.subscriptions().find((sub) => sub.subscriptionId === subscriptionId);
        if (subscription) {
          this.openDdosDialog(subscription);
        }
      }
    }
  }

  protected toggleClick(subscription: SurfCertType) {
    if (!this.hasCorrectRole) {
      return false;
    }

    if (subscription.productType === "IP") {
      if (!this.auth.selfServiceStarted()) {
        this.dialog.open(DialogAuthComponent, {
          data: {
            selfserviceState: { subscription },
            initialAction: "certFilter",
          },
        });
      } else {
        this.openDdosDialog(subscription);
      }
    } else {
      this.showUnchangeableDialog();
    }
  }

  private showUnchangeableDialog() {
    this.dialog.open(DialogMessageComponent, {
      data: {
        title: this.translate.instant("Cert.DDOSSecurity"),
        message: this.translate.instant("Cert.Unchangeable"),
      },
    });
  }

  private openDdosDialog(subscription: SurfCertType) {
    const data = {
      type: "ddos",
      subscription: {
        customerId: subscription.customerId,
        description: "",
        insync: subscription.insync,
        ipss: {
          surfcertFilterEnabled: subscription.surfcertFilterEnabled,
        },
        product: {
          type: subscription.productType,
          tag: subscription.productTag,
        },
        subscriptionId: subscription.subscriptionId,
      },
    };

    const dialogRef = openSelfServiceDialog(this.dialog, data);
    dialogRef.afterClosed().subscribe((event: string) => {
      if (event === "refresh") {
        this.certResource.reload();
      }
    });
  }

  protected routeToService({ productType, productTag, subscriptionId }) {
    const path = phraseDetailPath(productType, productTag, subscriptionId);
    this.router.navigate(path);
  }
}
